import React, { useMemo, useState } from "react";
import Select, { components } from "react-select";
import { MDI_CDN_BASE_URL, ICON_GROUPS } from "../../Data/mdiIcons";

const MDI_CDN = MDI_CDN_BASE_URL;

/** Convert mdiTelevision → television, mdiAirConditioner → air-conditioner */
const mdiToSlug = (name) =>
  name
    .replace(/^mdi/, "")
    .replace(/([A-Z])/g, (m) => `-${m.toLowerCase()}`)
    .replace(/^-/, "");

/** Convert mdiTelevision → Television, mdiAirConditioner → Air Conditioner */
const mdiToLabel = (name) =>
  name
    .replace(/^mdi/, "")
    .replace(/([A-Z])/g, (m) => ` ${m}`)
    .trim();

// Flatten to a map for fast lookup by value
const buildOptions = () =>
  ICON_GROUPS.map((group) => ({
    label: group.label,
    options: group.icons.map((name) => ({
      value: name,
      label: mdiToLabel(name),
      slug: mdiToSlug(name),
    })),
  }));

// ─────────────────────────────────────────────────────────────────────────────
// Custom Option: shows CDN icon image + readable label + mdi code name
// ─────────────────────────────────────────────────────────────────────────────
const IconImg = ({ slug, size = 20 }) => {
  const [visible, setVisible] = useState(true);
  if (!visible) return <span style={{ width: size, height: size, display: "inline-block" }} />;
  return (
    <img
      src={`${MDI_CDN}/${slug}.svg`}
      width={size}
      height={size}
      alt=""
      style={{ flexShrink: 0, opacity: 0.85 }}
      onError={() => setVisible(false)}
    />
  );
};

const CustomOption = (props) => {
  const { data, isFocused, isSelected, innerRef, innerProps } = props;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "6px 12px",
        cursor: "pointer",
        backgroundColor: isSelected ? "#e0e7ff" : isFocused ? "#f1f5f9" : "white",
        borderBottom: "1px solid #f8fafc",
      }}
    >
      <IconImg slug={data.slug} size={22} />
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
        <span style={{ fontSize: "0.88rem", fontWeight: 500, color: "#1e293b" }}>
          {data.label}
        </span>
        <span style={{ fontSize: "0.72rem", color: "#64748b", fontFamily: "monospace" }}>
          {data.value}
        </span>
      </div>
    </div>
  );
};

const CustomSingleValue = ({ data }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <IconImg slug={data.slug} size={20} />
    <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
      <span style={{ fontSize: "0.88rem", fontWeight: 500 }}>{data.label}</span>
      <span style={{ fontSize: "0.72rem", color: "#64748b", fontFamily: "monospace" }}>
        {data.value}
      </span>
    </div>
  </div>
);

// Custom GroupHeading to style category labels
const CustomGroupHeading = (props) => (
  <components.GroupHeading {...props}>
    <span style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#475569" }}>
      {props.children}
    </span>
  </components.GroupHeading>
);

// ─────────────────────────────────────────────────────────────────────────────
// MdiIconPicker
// ─────────────────────────────────────────────────────────────────────────────
const MdiIconPicker = ({ value, onChange, disabled }) => {
  const groupedOptions = useMemo(() => buildOptions(), []);

  // Find the currently selected option across all groups
  const selectedOption = useMemo(() => {
    if (!value) return null;
    for (const group of groupedOptions) {
      const found = group.options.find((o) => o.value === value);
      if (found) return found;
    }
    // If the stored value isn't in our curated list, create a synthetic option
    return { value, label: mdiToLabel(value), slug: mdiToSlug(value) };
  }, [value, groupedOptions]);

  const filterOption = (option, inputValue) => {
    if (!inputValue) return true;
    const q = inputValue.toLowerCase();
    return (
      option.data.label.toLowerCase().includes(q) ||
      option.data.value.toLowerCase().includes(q)
    );
  };

  return (
    <Select
      options={groupedOptions}
      value={selectedOption}
      onChange={(opt) => onChange(opt ? opt.value : "")}
      placeholder="Search icons (e.g. Television, Fan, Lock)..."
      isClearable
      isSearchable
      isDisabled={disabled}
      filterOption={filterOption}
      components={{
        Option: CustomOption,
        SingleValue: CustomSingleValue,
        GroupHeading: CustomGroupHeading,
      }}
      menuPortalTarget={document.body}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        menu: (base) => ({ ...base, zIndex: 9999, maxHeight: 320 }),
        menuList: (base) => ({ ...base, maxHeight: 320, padding: 0 }),
        control: (base, state) => ({
          ...base,
          borderColor: state.isFocused ? "#6366f1" : "#ced4da",
          boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(99,102,241,0.25)" : "none",
          "&:hover": { borderColor: "#6366f1" },
          minHeight: 44,
        }),
        singleValue: (base) => ({ ...base, overflow: "visible" }),
        clearIndicator: (base) => ({ ...base, cursor: "pointer" }),
      }}
    />
  );
};

export default MdiIconPicker;
